/**
 * Pure utility functions for player linking and table seating.
 * Extracted from PlayersView so they can be unit-tested without mounting the component.
 */

/**
 * Set the linked-user state on a player based on the tournament runner's allowed player options.
 * If the player's name matches an allowed option alias the player is linked (linkedUserId /
 * linkedUserAlias are set); otherwise those fields are removed.
 * Mutates the player object in place.
 *
 * @param {object} player - Player object from the players array
 * @param {{ alias: string, user_id: string }[]} allowedPlayerOptions
 */
export function setLinkedState(player, allowedPlayerOptions) {
  const name = (player.name || '').trim();
  if (!name) {
    delete player.linkedUserId;
    delete player.linkedUserAlias;
    return;
  }
  const option = allowedPlayerOptions.find(o => (o.alias || '').trim() === name);
  if (option?.user_id) {
    player.linkedUserId = option.user_id;
    player.linkedUserAlias = option.alias;
  } else {
    delete player.linkedUserId;
    delete player.linkedUserAlias;
  }
}

/**
 * Calculate how many tables should be used to seat a given number of players.
 * Returns 0 when table management is disabled (maxTables === 0).
 *
 * @param {number} playerCount - Number of active players
 * @param {number} maxPlayersPerTable
 * @param {number} maxTables - 0 means table management is disabled
 * @returns {number}
 */
export function computeTablesNeeded(playerCount, maxPlayersPerTable, maxTables) {
  if (maxTables === 0) return 0;
  const needed = Math.ceil(playerCount / maxPlayersPerTable);
  return Math.min(needed, maxTables);
}

/**
 * After a player busts, determine whether the bust created a table imbalance.
 * Returns true only when another table has 2 or more players than the busted
 * player's table (i.e. a rebalance move is worth suggesting). The busted player
 * must already be excluded from the playersByTable map before this is called.
 *
 * @param {Record<number, object[]>} playersByTable - map of tableNumber → active players (busted player already removed)
 * @param {number|null} bustedTable - table number the busted player was seated at
 * @returns {boolean}
 */
export function isTableUnbalancedAfterBust(playersByTable, bustedTable) {
  if (!bustedTable) return false;
  const bustedCount = (playersByTable[bustedTable] || []).length;
  if (bustedCount === 0) return false; // empty table is not a balance concern
  return Object.entries(playersByTable).some(
    ([table, players]) => Number(table) !== bustedTable && players.length > 0 && players.length >= bustedCount + 2
  );
}

/**
 * Assign table and seat numbers to players via round-robin distribution.
 * Players should already be in the desired seating order (e.g. pre-shuffled).
 * Mutates each player object in place.
 *
 * @param {object[]} players - Active players to seat
 * @param {number} tablesToUse - Number of tables to distribute across (0 = no-op)
 * @param {number} [maxSeatsPerTable=0] - When > 0, seat numbers are drawn from a
 *   shuffled pool of 1…maxSeatsPerTable so empty seats can appear anywhere at the
 *   table rather than always at the end. When 0, seats are assigned sequentially.
 */
export function distributePlayersToTables(players, tablesToUse, maxSeatsPerTable = 0) {
  if (tablesToUse === 0 || players.length === 0) return;

  // Group players by table via round-robin
  const byTable = {};
  let tableNum = 1;
  players.forEach(player => {
    player.table = tableNum;
    (byTable[tableNum] = byTable[tableNum] || []).push(player);
    tableNum = (tableNum % tablesToUse) + 1;
  });

  // Assign seats within each table
  Object.values(byTable).forEach(tablePlayers => {
    if (maxSeatsPerTable > 0) {
      // Shuffle a pool of all seat numbers so empty seats are distributed randomly
      const pool = Array.from({ length: maxSeatsPerTable }, (_, i) => i + 1);
      for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
      }
      tablePlayers.forEach((player, i) => { player.seat = pool[i] ?? i + 1; });
    } else {
      tablePlayers.forEach((player, i) => { player.seat = i + 1; });
    }
  });
}
